import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Star, Eye, EyeOff, Trash2, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";

function Stars({ value }) {
  return (
    <div className="inline-flex gap-0.5" aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`w-4 h-4 ${i <= value ? "text-ei-golddark fill-ei-golddark" : "text-ei-mist"}`} />
      ))}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}

export default function ReviewsModeration() {
  const [reviews, setReviews] = useState([]);
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get("/reviews/admin");
      setReviews(data.reviews);
      setPending(data.pending);
    } catch (e) {
      toast.error(formatApiError(e, "Could not load reviews"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setApproved = async (r, approved) => {
    setBusy(r.id);
    try {
      await api.patch(`/reviews/${r.id}`, { approved });
      toast.success(approved ? "Review approved — now visible publicly" : "Review hidden from the site");
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Could not update review"));
    } finally { setBusy(null); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Permanently delete this review from ${r.name}? This cannot be undone.`)) return;
    setBusy(r.id);
    try {
      await api.delete(`/reviews/${r.id}`);
      toast.success("Review deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Could not delete review"));
    } finally { setBusy(null); }
  };

  const shown = reviews.filter((r) =>
    filter === "all" ? true : filter === "pending" ? !r.approved : r.approved
  );

  const counts = {
    all: reviews.length,
    pending: reviews.filter((r) => !r.approved).length,
    approved: reviews.filter((r) => r.approved).length,
  };

  return (
    <div>
      <Seo title="Reviews — EI Battery Workspace" />
      <span className="ei-eyebrow">Moderation</span>
      <h1 className="mt-1 text-2xl sm:text-3xl font-bold text-ei-navy">Customer reviews</h1>
      <p className="mt-2 text-sm text-ei-graphite/70 max-w-2xl">
        New reviews stay hidden until you approve them. Approve a review to publish it on the public site, hide it to
        take it down, or delete it permanently.
      </p>

      {pending > 0 && (
        <div className="mt-4 inline-flex items-center gap-2 rounded-sm bg-ei-gold/15 border border-ei-gold/40 px-3 py-2 text-sm font-semibold text-ei-navy" data-testid="reviews-pending-banner">
          <Clock className="w-4 h-4 text-ei-golddark" /> {pending} review{pending !== 1 ? "s" : ""} awaiting approval
        </div>
      )}

      <div className="mt-6 flex gap-2" data-testid="reviews-filter">
        {[
          { k: "all", label: "All" },
          { k: "pending", label: "Pending" },
          { k: "approved", label: "Approved" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setFilter(t.k)}
            data-testid={`reviews-filter-${t.k}`}
            className={`font-display text-sm font-semibold px-4 py-2 rounded-sm border min-h-[40px] transition-colors ${
              filter === t.k ? "bg-ei-navy text-ei-white border-ei-navy" : "bg-white text-ei-navy border-ei-mist hover:border-ei-navy"
            }`}
          >
            {t.label} <span className="opacity-70">({counts[t.k]})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-ei-navy" /></div>
      ) : shown.length === 0 ? (
        <p className="mt-8 text-sm text-ei-graphite/60" data-testid="reviews-empty">No reviews in this view.</p>
      ) : (
        <ul className="mt-6 space-y-4" data-testid="staff-reviews-list">
          {shown.map((r) => (
            <li key={r.id} className="bg-white border border-ei-mist rounded-md p-5" data-testid={`staff-review-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-3">
                    <Stars value={r.rating} />
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-sm ${r.approved ? "bg-green-100 text-green-800" : "bg-ei-gold/20 text-ei-golddark"}`}
                      data-testid={`review-status-${r.id}`}
                    >
                      {r.approved ? "Approved" : "Pending"}
                    </span>
                  </div>
                  <p className="mt-3 text-sm text-ei-graphite/90 leading-relaxed">“{r.review}”</p>
                  <p className="mt-2 text-sm font-semibold text-ei-navy">
                    {r.name}
                    {r.company ? <span className="font-normal text-ei-graphite/60"> · {r.company}</span> : null}
                    <span className="font-normal text-ei-graphite/45"> · {fmtDate(r.created_at)}</span>
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {r.approved ? (
                    <button
                      onClick={() => setApproved(r, false)}
                      disabled={busy === r.id}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-sm border border-ei-mist hover:border-ei-navy min-h-[40px] disabled:opacity-40"
                      data-testid={`review-hide-${r.id}`}
                    >
                      <EyeOff className="w-3.5 h-3.5" /> Hide
                    </button>
                  ) : (
                    <button
                      onClick={() => setApproved(r, true)}
                      disabled={busy === r.id}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-sm bg-ei-navy text-ei-white hover:bg-ei-ink min-h-[40px] disabled:opacity-40"
                      data-testid={`review-approve-${r.id}`}
                    >
                      <Eye className="w-3.5 h-3.5" /> Approve
                    </button>
                  )}
                  <button
                    onClick={() => remove(r)}
                    disabled={busy === r.id}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-sm border border-red-200 text-red-700 hover:border-red-400 min-h-[40px] disabled:opacity-40"
                    data-testid={`review-delete-${r.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
